/** Какие правила показывать в Rules Manager для выбранных сущностей.
 *
 *  Оба предиката живут здесь, а не в модальном окне, по одной причине:
 *  их можно прогнать (`src/lib/**` — область прогона фронтенда), а
 *  ошибка в них не видна глазами. Ошибиться тут стоит дорого в обе
 *  стороны: спрятанное правило пользователь не найдёт, показанное
 *  лишнее — отвергнет сервер.
 */

/** Глубина в дереве ставок: campaign(0) → ad_group(1) → keyword(2). */
const GRAIN_DEPTH: Record<string, number> = {
  campaign: 0,
  ad_group: 1,
  keyword: 2,
};

export type Grain = "campaign" | "ad_group" | "keyword" | "search_term";

/** Правило уровня `ruleLevel` применимо на `grain`, если оно того же
 *  уровня или глубже — привязка спускается вниз.
 *
 *  ★ Правило по поисковым запросам применимо ещё и к кампании, и к
 *  группе: очередь спускается от привязки к каждому запросу под этой
 *  областью. Именно так применяются пресеты Broad to SKAG Extractor и
 *  Search Term Cleaner. К ключу — нет: ключ не предок запроса, ветки
 *  под него в очереди не существует, и привязка не выстрелила бы
 *  никогда. Зеркало — `_level_compatible` на сервере. */
export function levelCompatible(grain: Grain, ruleLevel: string): boolean {
  if (ruleLevel === "search_term") {
    return grain === "search_term" || grain === "campaign" || grain === "ad_group";
  }
  if (grain === "search_term") return false;
  const rd = GRAIN_DEPTH[ruleLevel];
  const gd = GRAIN_DEPTH[grain];
  if (rd === undefined || gd === undefined) return false;
  return rd >= gd;
}

/** Правило подходит приложению выбранных сущностей.
 *
 *  ★ Правило принадлежит ОДНОМУ приложению (§23.1): сервер отвергает
 *  привязку к чужому, потому что Go-to-Goal ищет настройку целей по
 *  паре (организация, приложение). Показывать чужое правило значит
 *  предлагать действие, которое заведомо откажет.
 *
 *  `ruleAppId === null` — правило ещё ни к чему не привязано и подойдёт
 *  любому приложению. `appId === null` — приложение неизвестно (вид
 *  «все приложения», разнородное выделение): не фильтруем, заслон
 *  остаётся на сервере. */
export function ruleFitsApp(
  ruleAppId: number | null | undefined,
  appId: number | null | undefined,
): boolean {
  if (appId == null) return true;
  return ruleAppId == null || ruleAppId === appId;
}

/** Пресеты без привязок: работают по списку приложений, привязка к
 *  кампании им ничего не даёт. Зеркало — `NOT_ATTACHABLE_PRESETS` в
 *  `rules_service.py`. Exact Terms Cleaner сюда НЕ входит: он работает
 *  именно по привязкам. */
const NOT_ATTACHABLE_PRESETS = new Set(["duplicate_keyword_guard"]);

type ChainableRule = {
  id: number;
  name: string;
  preset?: string | null;
  app_id: number | null;
  resolved_app_id?: number | null;
};

/** Правила, которые Broad to SKAG Extractor может привязать к каждой
 *  созданной кампании: все правила аккаунта, подходящие приложению
 *  пресета, кроме самого пресета и правил без привязок. Уже выбранные
 *  остаются в списке, даже если перестали подходить, — иначе их нельзя
 *  было бы снять. По имени, без учёта регистра. */
export function chainableRules<R extends ChainableRule>(
  rules: R[],
  { appId, selfId, selected }: { appId: number | null; selfId: number | null; selected: number[] },
): R[] {
  const keep = new Set(selected);
  return rules
    .filter((r) => r.id !== selfId)
    .filter((r) => keep.has(r.id) || !NOT_ATTACHABLE_PRESETS.has(r.preset ?? ""))
    .filter((r) => keep.has(r.id) || ruleFitsApp(r.resolved_app_id ?? r.app_id, appId))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
}
